import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, Loader2, Award, Users, Globe, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/eabq3wxl_FINRUN%20LOGO%20PNG%202.png";

const SponsorPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [honeypot, setHoneypot] = useState(""); // Anti-spam honeypot
  
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    sponsorship_tier: "",
    website: "",
    message: ""
  });

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await axios.get(`${API}/sponsorship-tiers`);
        setTiers(response.data.tiers);
      } catch (e) {
        console.error("Error fetching tiers:", e);
      }
    };
    fetchTiers();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const selectedTier = tiers.find(t => t.id === formData.sponsorship_tier);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Anti-spam: If honeypot is filled, it's a bot
    if (honeypot) {
      toast.success("Sponsor inquiry submitted successfully!"); // Fake success for bots
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/sponsor-inquiries`, formData);
      setSubmitted(true);
      toast.success("Sponsor inquiry submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="container-custom section-spacing pt-32">
          <Card className="max-w-2xl mx-auto text-center" data-testid="sponsor-success">
            <CardContent className="p-12">
              <img 
                src={LOGO_URL} 
                alt="The FinRun" 
                className="h-20 w-auto mx-auto mb-6"
              />
              <div className="w-20 h-20 bg-[#007AFF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#007AFF]" />
              </div>
              <h1 className="text-4xl font-black text-[#0A0A0A] mb-4 font-['Outfit']">
                Thank You!
              </h1>
              <p className="text-[#4B5563] text-lg mb-8">
                We've received your sponsorship inquiry. Our partnership team will reach out within 48 hours to discuss how we can create an impactful collaboration.
              </p>
              <div className="bg-[#FAFAFA] p-6 rounded-2xl mb-8 text-left">
                <h3 className="font-semibold mb-4">Your Inquiry Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#9CA3AF]">Company</p>
                    <p className="font-medium">{formData.company_name}</p>
                  </div>
                  <div>
                    <p className="text-[#9CA3AF]">Selected Tier</p>
                    <p className="font-medium">{selectedTier?.name || "Custom"}</p>
                  </div>
                  <div>
                    <p className="text-[#9CA3AF]">Contact</p>
                    <p className="font-medium">{formData.contact_person}</p>
                  </div>
                  <div>
                    <p className="text-[#9CA3AF]">Email</p>
                    <p className="font-medium">{formData.email}</p>
                  </div>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/")} 
                className="btn-secondary"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="sponsor-page">
      <Navbar />
      
      {/* Hero Banner */}
      <div className="bg-[#111827] pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.pexels.com/photos/5380642/pexels-photo-5380642.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="text-center">
            <p className="text-[#007AFF] text-sm uppercase tracking-[0.2em] font-bold mb-4">
              Partner With Purpose
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 font-['Outfit']">
              Become a <span className="text-[#FFD700]">Sponsor</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Join The FinRun as a sponsor and be part of India's largest movement 
              combining fitness with cybersecurity awareness.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-[#FF3B30] py-6">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div>
              <p className="text-3xl font-black font-['Outfit']">10,000+</p>
              <p className="text-sm opacity-80">Expected Participants</p>
            </div>
            <div>
              <p className="text-3xl font-black font-['Outfit']">5M+</p>
              <p className="text-sm opacity-80">Social Reach</p>
            </div>
            <div>
              <p className="text-3xl font-black font-['Outfit']">3</p>
              <p className="text-sm opacity-80">Cities</p>
            </div>
            <div>
              <p className="text-3xl font-black font-['Outfit']">50+</p>
              <p className="text-sm opacity-80">Media Partners</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-bold font-['Outfit']">
                  Sponsorship Inquiry
                </CardTitle>
                <CardDescription>
                  Tell us about your organization and partnership interests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - hidden from humans, catches bots */}
                  <input
                    type="text"
                    name="company_url"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{ position: 'absolute', left: '-9999px' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  
                  {/* Company Info */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company Name *</Label>
                      <Input 
                        id="company_name"
                        value={formData.company_name}
                        onChange={(e) => handleChange("company_name", e.target.value)}
                        required
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input 
                        id="website"
                        type="url"
                        placeholder="https://"
                        value={formData.website}
                        onChange={(e) => handleChange("website", e.target.value)}
                        data-testid="website-input"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_person">Contact Person *</Label>
                      <Input 
                        id="contact_person"
                        value={formData.contact_person}
                        onChange={(e) => handleChange("contact_person", e.target.value)}
                        required
                        data-testid="contact-person-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Business Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        required
                        data-testid="sponsor-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input 
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        required
                        data-testid="sponsor-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sponsorship_tier">Sponsorship Tier *</Label>
                      <Select 
                        value={formData.sponsorship_tier} 
                        onValueChange={(v) => handleChange("sponsorship_tier", v)}
                      >
                        <SelectTrigger data-testid="tier-select">
                          <SelectValue placeholder="Select tier" />
                        </SelectTrigger>
                        <SelectContent>
                          {tiers.map(tier => (
                            <SelectItem key={tier.id} value={tier.id}>
                              {tier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Selected Tier Benefits */}
                  {selectedTier && (
                    <div className="bg-[#007AFF]/5 border border-[#007AFF]/20 rounded-xl p-4">
                      <h4 className="font-semibold text-[#007AFF] mb-2">{selectedTier.name} Benefits</h4>
                      <p className="text-sm text-[#4B5563] mb-3">{selectedTier.description}</p>
                      <ul className="space-y-1">
                        {selectedTier.benefits.map((benefit, i) => (
                          <li key={i} className="text-sm flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-[#007AFF]" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="message">Additional Information</Label>
                    <Textarea 
                      id="message"
                      placeholder="Tell us about your sponsorship goals, target audience, or any specific requirements..."
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      rows={4}
                      data-testid="sponsor-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-secondary text-lg py-6"
                    disabled={loading}
                    data-testid="submit-sponsor-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Inquiry"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Why Sponsor */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-6 font-['Outfit']">Why Sponsor The FinRun?</h3>
                <div className="space-y-5">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-[#FF3B30]" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Massive Reach</h4>
                      <p className="text-sm text-[#4B5563]">Connect with 10,000+ runners, tech professionals, and cyber-aware citizens</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-[#007AFF]" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">CSR Impact</h4>
                      <p className="text-sm text-[#4B5563]">Align with a meaningful cause combating India's cybercrime crisis</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#FFD700]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-[#B8860B]" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Brand Visibility</h4>
                      <p className="text-sm text-[#4B5563]">Premium placement across digital, print, and on-ground touchpoints</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Megaphone className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Media Coverage</h4>
                      <p className="text-sm text-[#4B5563]">Extensive PR and media amplification across channels</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="border-0 shadow-xl bg-[#111827] text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 font-['Outfit']">Direct Contact</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Prefer to speak directly? Reach out to our partnership team.
                </p>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-[#007AFF]">info@thefinrun.com</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-[#FFD700]">+91 9618474761</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Website</p>
                    <p className="text-white">www.thefinrun.com</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Past Sponsors */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 font-['Outfit']">Past Partners</h3>
                <p className="text-sm text-[#4B5563] mb-4">
                  Join the league of organizations that have supported our mission.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {["SBI Life", "Decathlon", "Zycle", "AIG", "T-Hub", "CyberPeace"].map((name, i) => (
                    <div 
                      key={i} 
                      className="bg-[#FAFAFA] rounded-lg p-3 text-center text-xs font-medium text-[#4B5563]"
                    >
                      {name}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SponsorPage;
